import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Pencil, X, Wrench } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SERVICE_ICONS, ICON_KEYS, ICON_LABELS } from "@/lib/icons";

const field =
  "w-full border border-white/10 bg-black/50 px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-rose-500";
const lbl = "font-mono-t text-[10px] uppercase tracking-[0.18em] text-slate-500";
const EMPTY = { title: "", subtitle: "", icon: "wrench", order: 10, rows: [{ group: "", label: "", price: "" }] };

export const PriceAdmin = ({ categories = [], onReload }) => {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const patchRow = (i, patch) =>
    setEditing((c) => ({ ...c, rows: c.rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)) }));
  const addRow = () => setEditing((c) => ({ ...c, rows: [...c.rows, { group: "", label: "", price: "" }] }));
  const delRow = (i) => setEditing((c) => ({ ...c, rows: c.rows.filter((_, ri) => ri !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: editing.title.trim(),
      subtitle: (editing.subtitle || "").trim(),
      icon: editing.icon,
      order: Number(editing.order) || 0,
      rows: editing.rows
        .filter((r) => r.label.trim())
        .map((r) => ({ group: (r.group || "").trim(), label: r.label.trim(), price: (r.price || "").trim() })),
    };
    try {
      if (editing.id) await api.put(`/prices/${editing.id}`, payload);
      else await api.post("/prices", payload);
      toast.success("Cenrādis saglabāts");
      setEditing(null);
      onReload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/prices/${id}`);
      toast.success("Kategorija dzēsta");
      onReload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="border border-white/10 bg-[#0c0c11] p-5 sm:p-6" data-testid="price-admin-form">
        <div className="flex items-center justify-between">
          <h3 className="font-head text-xl font-bold uppercase tracking-tight text-white">
            {editing.id ? "Labot kategoriju" : "Jauna kategorija"}
          </h3>
          <button type="button" onClick={() => setEditing(null)} data-testid="price-cancel" className="text-slate-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={lbl}>Kategorijas nosaukums</span>
            <input
              className={`${field} mt-1.5`}
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              data-testid="price-title"
              required
            />
          </label>
          <label className="block">
            <span className={lbl}>Kārtas numurs</span>
            <input
              type="number"
              className={`${field} mt-1.5`}
              value={editing.order}
              onChange={(e) => setEditing({ ...editing, order: e.target.value })}
              data-testid="price-order"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className={lbl}>Apakšvirsraksts (nav obligāts)</span>
            <input
              className={`${field} mt-1.5`}
              value={editing.subtitle || ""}
              onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
              data-testid="price-subtitle"
            />
          </label>
        </div>

        <div className="mt-5">
          <span className={lbl}>Ikona</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {ICON_KEYS.map((k) => {
              const Icon = SERVICE_ICONS[k] || Wrench;
              return (
                <button
                  key={k}
                  type="button"
                  title={ICON_LABELS[k] || k}
                  onClick={() => setEditing({ ...editing, icon: k })}
                  data-testid={`price-icon-${k}`}
                  className={`flex h-10 w-10 items-center justify-center border transition-colors ${
                    editing.icon === k ? "border-rose-500 bg-rose-600/15 text-rose-400" : "border-white/10 text-slate-500 hover:border-white/30"
                  }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-7">
          <div className="flex items-center justify-between">
            <span className={lbl}>Pozīcijas ({editing.rows.length})</span>
            <button
              type="button"
              onClick={addRow}
              data-testid="price-add-row"
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono-t text-[10px] uppercase tracking-[0.18em] text-slate-300 hover:border-rose-500 hover:text-rose-300"
            >
              <Plus className="h-3.5 w-3.5" /> Pievienot rindu
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {editing.rows.map((r, i) => (
              <div key={i} className="grid gap-2 border border-white/10 bg-black/30 p-3 sm:grid-cols-[1fr_1.4fr_0.8fr_auto]">
                <input
                  className={field}
                  placeholder="Apakšgrupa (nav obligāta)"
                  value={r.group || ""}
                  onChange={(e) => patchRow(i, { group: e.target.value })}
                  data-testid={`price-row-group-${i}`}
                />
                <input
                  className={field}
                  placeholder="Darba nosaukums"
                  value={r.label}
                  onChange={(e) => patchRow(i, { label: e.target.value })}
                  data-testid={`price-row-label-${i}`}
                />
                <input
                  className={field}
                  placeholder="no 25,00 €"
                  value={r.price || ""}
                  onChange={(e) => patchRow(i, { price: e.target.value })}
                  data-testid={`price-row-price-${i}`}
                />
                <button
                  type="button"
                  onClick={() => delRow(i)}
                  data-testid={`price-row-delete-${i}`}
                  className="flex items-center justify-center border border-white/10 px-3 text-slate-500 hover:border-rose-500 hover:text-rose-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          data-testid="price-save"
          className="mt-7 inline-flex items-center gap-2 border border-rose-500 bg-rose-600 px-6 py-3 font-head text-sm font-bold uppercase tracking-tight text-white transition-colors hover:bg-rose-500 disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> Saglabāt
        </button>
      </form>
    );
  }

  return (
    <div data-testid="price-admin">
      <button
        onClick={() => setEditing({ ...EMPTY, order: (categories.length + 1) * 1 })}
        data-testid="price-new"
        className="inline-flex items-center gap-2 border border-rose-500 bg-rose-600/15 px-5 py-2.5 font-mono-t text-[10px] uppercase tracking-[0.2em] text-rose-300 transition-colors hover:bg-rose-600 hover:text-white"
      >
        <Plus className="h-4 w-4" /> Jauna kategorija
      </button>

      <div className="mt-5 space-y-3">
        {categories.map((c) => {
          const Icon = SERVICE_ICONS[c.icon] || Wrench;
          return (
            <div
              key={c.id}
              className="flex items-center gap-4 border border-white/10 bg-[#0c0c11] p-4"
              data-testid={`price-admin-item-${c.id}`}
            >
              <span className="font-mono-t text-[10px] text-slate-600">{c.order}</span>
              <Icon className="h-6 w-6 shrink-0 text-rose-500" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-head text-lg font-bold uppercase tracking-tight text-slate-100">
                  {c.title}
                </div>
                <div className="font-mono-t text-[9px] uppercase tracking-[0.2em] text-slate-500">
                  {c.rows?.length || 0} pozīcijas
                </div>
              </div>
              <button
                onClick={() => setEditing({ ...c, rows: c.rows?.length ? c.rows.map((r) => ({ ...r })) : [{ group: "", label: "", price: "" }] })}
                data-testid={`price-edit-${c.id}`}
                className="border border-white/15 p-2 text-slate-400 hover:border-rose-500 hover:text-rose-400"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={() => remove(c.id)}
                data-testid={`price-delete-${c.id}`}
                className="border border-white/15 p-2 text-slate-400 hover:border-rose-500 hover:text-rose-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
